import React, { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { motion, AnimatePresence } from "framer-motion";
import { MdExpandMore } from "react-icons/md";
import { BsPinAngle, BsPinAngleFill } from "react-icons/bs";
import { CategoryTreeApi } from "api/categories/categoriesApi";
import { useCategoryTree } from "api/categories/useCategoryTree";

interface CategoriesChipsProps {
  onSelect: (id: number, allIds: number[]) => void;
  customCategories?: { id: number; name: string }[];
  selected: number;
  pinStorageKey?: string;
}

const collectAllIds = (node: CategoryTreeApi): number[] => {
  const ids = [node.id];
  for (const child of node.children ?? []) {
    ids.push(...collectAllIds(child));
  }
  return ids;
};

const collectAllTreeIds = (nodes: CategoryTreeApi[]): number[] => {
  const ids: number[] = [];
  for (const node of nodes) {
    ids.push(...collectAllIds(node));
  }
  return ids;
};

const loadPinned = (key: string): Set<number> => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as number[]);
  } catch {
    return new Set();
  }
};

const savePinned = (key: string, pinned: Set<number>) => {
  localStorage.setItem(key, JSON.stringify([...pinned]));
};

interface TreeNodeProps {
  node: CategoryTreeApi;
  depth: number;
  selected: number;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onNodeSelect: (node: CategoryTreeApi) => void;
  pinned: Set<number>;
  onPin: (id: number) => void;
  showPinControls: boolean;
}

const TreeNode = ({
  node,
  depth,
  selected,
  expanded,
  onToggle,
  onNodeSelect,
  pinned,
  onPin,
  showPinControls,
}: TreeNodeProps) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);
  const isSelected = selected === node.id;
  const isPinned = pinned.has(node.id);

  return (
    <div>
      <div
        className={`group flex items-center rounded-md transition-colors duration-150 ${
          isSelected ? "bg-primary-100 dark:bg-primary-700/40" : "hover:bg-slate-100 dark:hover:bg-slate-700/50"
        }`}
        style={{ paddingLeft: `${depth * 20}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.id);
            }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
          >
            <motion.span
              animate={{ rotate: isExpanded ? 0 : -90 }}
              transition={{ duration: 0.15 }}
              className="flex items-center"
            >
              <MdExpandMore size={18} />
            </motion.span>
          </button>
        ) : (
          <span className="w-7 shrink-0" />
        )}
        <button
          onClick={() => onNodeSelect(node)}
          className={`flex-1 truncate py-1.5 pr-1 text-left text-sm transition-colors duration-150 ${
            isSelected ? "font-semibold text-primary-600 dark:text-primary-400" : "text-slate-700 dark:text-slate-300"
          } ${depth === 0 ? "font-medium" : ""}`}
        >
          {node.name}
          {hasChildren && isSelected && (
            <span className="ml-1.5 text-xs font-normal text-slate-400 dark:text-slate-500">(wszystkie)</span>
          )}
        </button>
        {showPinControls && depth === 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPin(node.id);
            }}
            className={`mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors duration-150 ${
              isPinned
                ? "text-primary-500 dark:text-primary-400"
                : "text-slate-300 opacity-0 group-hover:opacity-100 dark:text-slate-600"
            }`}
            title={isPinned ? "Odepnij" : "Przypnij"}
          >
            {isPinned ? <BsPinAngleFill size={13} /> : <BsPinAngle size={13} />}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            {node.children!.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                selected={selected}
                expanded={expanded}
                onToggle={onToggle}
                onNodeSelect={onNodeSelect}
                pinned={pinned}
                onPin={onPin}
                showPinControls={showPinControls}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CategoriesChips = ({
  onSelect,
  customCategories = [],
  selected,
  pinStorageKey = "categories-pinned",
}: CategoriesChipsProps) => {
  const { isLoading, tree } = useCategoryTree();
  const [pinned, setPinned] = useState<Set<number>>(() => loadPinned(pinStorageKey));
  const [showAllCategories, setShowAllCategories] = useState(false);

  const hasPins = pinned.size > 0;
  const showPinControls = showAllCategories || !hasPins;

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [mobileOpen, setMobileOpen] = useState(false);
  const seenIdsRef = React.useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!tree.length) return;
    const allIds = collectAllTreeIds(tree);
    const newIds = allIds.filter((id) => !seenIdsRef.current.has(id));
    for (const id of allIds) seenIdsRef.current.add(id);
    if (newIds.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const id of newIds) next.add(id);
      return next;
    });
  }, [tree]);

  // Auto-select when exactly one root is pinned
  const autoSelectedOnce = React.useRef(false);
  useEffect(() => {
    if (autoSelectedOnce.current || !tree.length || pinned.size !== 1) return;
    const pinnedId = [...pinned][0];
    const node = tree.find((n) => n.id === pinnedId);
    if (!node) return;
    autoSelectedOnce.current = true;
    const allIds = collectAllIds(node);
    onSelect(node.id, allIds);
  }, [tree, pinned, onSelect]);

  const onToggle = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleNodeSelect = useCallback(
    (node: CategoryTreeApi) => {
      const allIds = collectAllIds(node);
      onSelect(node.id, allIds);
    },
    [onSelect],
  );

  const handleAllSelect = useCallback(() => {
    onSelect(-1, []);
  }, [onSelect]);

  const handlePin = useCallback(
    (id: number) => {
      setPinned((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        savePinned(pinStorageKey, next);
        return next;
      });
    },
    [pinStorageKey],
  );

  if (isLoading) {
    return (
      <div className="mb-3">
        <Skeleton count={4} height={28} containerClassName="flex flex-col gap-1" className="rounded-md" />
      </div>
    );
  }

  const visibleTree = hasPins && !showAllCategories ? tree.filter((node) => pinned.has(node.id)) : tree;

  const hiddenCount = hasPins ? tree.length - visibleTree.length : 0;

  return (
    <div className="mb-3 w-full rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-800 md:w-64">
      <button
        type="button"
        onClick={() => setMobileOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/50 md:hidden"
        aria-expanded={mobileOpen}
      >
        <span>Kategorie</span>
        <motion.span
          animate={{ rotate: mobileOpen ? 0 : -90 }}
          transition={{ duration: 0.15 }}
          className="flex items-center"
        >
          <MdExpandMore size={18} />
        </motion.span>
      </button>

      <div className={`${mobileOpen ? "block" : "hidden"} md:block`}>
        <div
          onClick={handleAllSelect}
          className={`flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sm font-medium transition-colors duration-150 ${
            selected === -1
              ? "bg-primary-100 text-primary-600 dark:bg-primary-700/40 dark:text-primary-400"
              : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/50"
          }`}
        >
          Wszystkie
        </div>

        {customCategories.map((cat) => (
          <div
            key={`custom-${cat.id}`}
            onClick={() => onSelect(cat.id, [cat.id])}
            className={`flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sm transition-colors duration-150 ${
              selected === cat.id
                ? "bg-primary-100 font-semibold text-primary-600 dark:bg-primary-700/40 dark:text-primary-400"
                : "text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700/50"
            }`}
          >
            {cat.name}
          </div>
        ))}

        {visibleTree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            depth={0}
            selected={selected}
            expanded={expanded}
            onToggle={onToggle}
            onNodeSelect={handleNodeSelect}
            pinned={pinned}
            onPin={handlePin}
            showPinControls={showPinControls}
          />
        ))}

        {hasPins && (
          <button
            onClick={() => setShowAllCategories((v) => !v)}
            className="mt-1 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
          >
            <BsPinAngle size={11} />
            {showAllCategories ? "Pokaż tylko przypięte" : `Pokaż wszystkie (+${hiddenCount} ukrytych)`}
          </button>
        )}
      </div>
    </div>
  );
};
